
import React, { createContext, useContext, useRef, useCallback } from "react";

import { useDispatch } from "react-redux";

import { show, type ConfirmState } from "./Confirm.slice";


export type ConfirmResult = "yes" | "no" | "cancel";

interface ConfirmContextType {
  confirm: (options: ConfirmState) => Promise<ConfirmResult>;
  setResolver: (resolver: ((result: ConfirmResult) => void) | null) => void;
  getResolver: () => ((result: ConfirmResult) => void) | null;
}

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export const ConfirmProvider: React.FC = ({ children }) => {
  const dispatch = useDispatch();
  const resolverRef = useRef<((result: ConfirmResult) => void) | null>(null);

  const setResolver = useCallback((resolver: ((result: ConfirmResult) => void) | null) => {
    resolverRef.current = resolver;
  }, []);

  const getResolver = useCallback(() => resolverRef.current, []);

  const confirm = useCallback((options: ConfirmState) => {
    dispatch(show(options));
    return new Promise<ConfirmResult>((resolve) => {
      resolverRef.current = resolve;
    });
  }, [ dispatch ]);

  return (
    <ConfirmContext.Provider value={{
      confirm,
      setResolver,
      getResolver
    }}>
      {children}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx.confirm;
};

// Hooks to access resolver setters and getters in Confirm container
export const useSetConfirmResolver = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useSetConfirmResolver must be used within ConfirmProvider");
  return ctx.setResolver;
};

export const useGetConfirmResolver = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useGetConfirmResolver must be used within ConfirmProvider");
  return ctx.getResolver;
};


